import React, { useState, useEffect } from "react";
import * as adminApi from "../../api/adminApi";
import { useToast } from "../../context/ToastContext";
import { formatProductSize } from "../../utils/format";
import { X, Check, Save, Layers, Palette, RefreshCw } from "lucide-react";
import ProductSearchPicker from "./ProductSearchPicker";

export default function FastStockUpdate({ onClose, onStockUpdated, initialProductCode = null, initialItem = null }) {
  const { addToast } = useToast();

  // Products catalog for picker
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Variant & stock states
  const [productData, setProductData] = useState(null);
  const [loadingVariants, setLoadingVariants] = useState(false);

  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [selectedColorId, setSelectedColorId] = useState(null);

  const [stockInfo, setStockInfo] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [newStock, setNewStock] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Load all products on mount
  useEffect(() => {
    let cancelled = false;
    setLoadingProducts(true);
    adminApi
      .fetchAdminProducts()
      .then((res) => {
        if (!cancelled) {
          const list = Array.isArray(res) ? res : [];
          setProducts(list);

          // If initialProductCode or initialItem provided, pre-select matching product
          const targetCode = initialProductCode || initialItem?.code;
          if (targetCode) {
            const match = list.find((p) => p.code?.toLowerCase() === targetCode.toLowerCase());
            if (match) {
              handleSelectProduct(match);
            }
          }
        }
      })
      .catch((err) => {
        if (!cancelled) addToast(err.message || "Failed to load products", "error");
      })
      .finally(() => {
        if (!cancelled) setLoadingProducts(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialProductCode, initialItem]);

  // When a product is selected
  const handleSelectProduct = async (product) => {
    setSelectedProduct(product);
    setProductData(null);
    setSelectedSizeId(initialItem?.sizeId || null);
    setSelectedColorId(initialItem?.colorId || null);
    setStockInfo(null);
    setNewStock("");
    setLoadingVariants(true);

    try {
      const res = await adminApi.fetchProductVariantsByCode(product.code);
      setProductData(res);

      // Auto-select single variant
      if (res.sizeVariants?.length === 1 && !initialItem?.sizeId) {
        setSelectedSizeId(res.sizeVariants[0].id);
      }
      if (res.colorVariants?.length === 1 && !initialItem?.colorId) {
        setSelectedColorId(res.colorVariants[0].id);
      }
    } catch (err) {
      addToast(err.message || "Failed to fetch variants for this product", "error");
    } finally {
      setLoadingVariants(false);
    }
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setProductData(null);
    setSelectedSizeId(null);
    setSelectedColorId(null);
    setStockInfo(null);
    setNewStock("");
  };

  // Load stock when both size and color are chosen
  useEffect(() => {
    if (selectedSizeId && selectedColorId && productData) {
      let active = true;
      setLoadingStock(true);
      setStockInfo(null);
      setNewStock("");

      adminApi
        .fetchVariantStock(productData.id, selectedColorId, selectedSizeId)
        .then((res) => {
          if (active) {
            setStockInfo(res);
            setNewStock(res.quantity.toString());
          }
        })
        .catch((err) => {
          if (active) {
            if (err.status === 404 || err.message?.includes("404")) {
              setStockInfo({ quantity: 0, lastEnterdQuantity: 0 });
              setNewStock("0");
            } else {
              addToast("Failed to load stock info", "error");
            }
          }
        })
        .finally(() => {
          if (active) setLoadingStock(false);
        });

      return () => {
        active = false;
      };
    }
  }, [selectedSizeId, selectedColorId, productData, addToast]);

  // Adjust stock with quick stepper
  const handleQuickAdjust = (delta) => {
    const currentVal = Number(newStock) || 0;
    const nextVal = Math.max(0, currentVal + delta);
    setNewStock(String(nextVal));
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!productData || !selectedColorId || !selectedSizeId) return;

    const stockNum = Number(newStock);
    if (isNaN(stockNum) || stockNum < 0) {
      addToast("Please enter a valid stock quantity (0 or greater)", "error");
      return;
    }

    setIsSaving(true);
    try {
      await adminApi.setAdminStock(productData.id, {
        colorId: selectedColorId,
        sizeId: selectedSizeId,
        newStockValue: stockNum,
      });
      addToast("Stock updated successfully!", "success");
      onStockUpdated?.();
      onClose();
    } catch (err) {
      addToast(err.message || "Failed to update stock", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const sizeVariants = productData?.sizeVariants || [];
  const colorVariants = productData?.colorVariants || [];

  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <div className="admin-modal fast-stock-modal">
        {/* Fixed Modal Header */}
        <div className="fast-stock-modal__head">
          <div>
            <h3 style={{ margin: 0, fontSize: "1.4rem" }}>Fast Stock Update</h3>
            <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--text-muted)" }}>
              {selectedProduct
                ? "Configure variant and update inventory quantity."
                : "Search or browse below to select a target product."}
            </p>
          </div>
          <button
            type="button"
            className="admin-icon-link"
            onClick={onClose}
            aria-label="Close modal"
            style={{ padding: "8px" }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="fast-stock-modal__body">
          {!selectedProduct ? (
            /* Mode 1: Product Picker with Fixed Search & Scrollable Results Below */
            <ProductSearchPicker
              products={products}
              selectedProduct={null}
              onSelectProduct={handleSelectProduct}
              onClearProduct={handleClearProduct}
              loading={loadingProducts}
            />
          ) : (
            /* Mode 2: Product Chosen -> Pinned Banner + Scrollable Variant & Stock Adjuster */
            <>
              <ProductSearchPicker
                products={products}
                selectedProduct={selectedProduct}
                onSelectProduct={handleSelectProduct}
                onClearProduct={handleClearProduct}
                loading={loadingProducts}
              />

              <div className="fast-stock-modal__body-scrollable">
                {loadingVariants ? (
                  <div style={{ padding: "40px 0", textAlign: "center", color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                    <span className="page-loading__dot" style={{ width: "10px", height: "10px", margin: 0 }} />
                    <span>Loading available sizes and colors...</span>
                  </div>
                ) : (
                  <>
                    {/* Sizes Selection */}
                    <div style={{ background: "var(--surface-card)", padding: "18px 20px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                        <Layers size={16} style={{ color: "var(--gold)" }} />
                        <h5 style={{ margin: 0, fontSize: "13px", color: "var(--text-primary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          1. Select Size Variant
                        </h5>
                        <span style={{ fontSize: "12px", color: "var(--text-muted)", marginLeft: "auto" }}>
                          {sizeVariants.length} option{sizeVariants.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {sizeVariants.length === 0 ? (
                        <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>No sizes configured for this product.</div>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                          {sizeVariants.map((s) => {
                            const isSelected = selectedSizeId === s.id;
                            return (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() => setSelectedSizeId(s.id)}
                                style={{
                                  padding: "10px 18px",
                                  border: isSelected ? "2px solid var(--gold)" : "1px solid var(--border)",
                                  background: isSelected ? "var(--gold)" : "var(--surface-input)",
                                  color: isSelected ? "#ffffff" : "var(--text-primary)",
                                  fontWeight: isSelected ? 600 : 400,
                                  borderRadius: "var(--radius)",
                                  fontSize: "14px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  boxShadow: isSelected ? "0 2px 8px rgba(201,169,110,0.3)" : "none",
                                  transition: "all 0.15s ease",
                                }}
                              >
                                {isSelected && <Check size={16} style={{ color: "#ffffff" }} />}
                                {formatProductSize(s.size)}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Colors Selection */}
                    <div style={{ background: "var(--surface-card)", padding: "18px 20px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                        <Palette size={16} style={{ color: "var(--gold)" }} />
                        <h5 style={{ margin: 0, fontSize: "13px", color: "var(--text-primary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          2. Select Color Variant
                        </h5>
                        <span style={{ fontSize: "12px", color: "var(--text-muted)", marginLeft: "auto" }}>
                          {colorVariants.length} option{colorVariants.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {colorVariants.length === 0 ? (
                        <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>No colors configured for this product.</div>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                          {colorVariants.map((c) => {
                            const isSelected = selectedColorId === c.id;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => setSelectedColorId(c.id)}
                                style={{
                                  padding: "10px 18px",
                                  border: isSelected ? "2px solid var(--gold)" : "1px solid var(--border)",
                                  background: isSelected ? "var(--gold)" : "var(--surface-input)",
                                  color: isSelected ? "#ffffff" : "var(--text-primary)",
                                  fontWeight: isSelected ? 600 : 400,
                                  borderRadius: "var(--radius)",
                                  fontSize: "14px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  boxShadow: isSelected ? "0 2px 8px rgba(201,169,110,0.3)" : "none",
                                  transition: "all 0.15s ease",
                                }}
                              >
                                {isSelected && <Check size={16} style={{ color: "#ffffff" }} />}
                                {c.color}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Stock Details & Quantity Adjustment */}
                    {selectedSizeId && selectedColorId && (
                      <div style={{ background: "var(--surface-card)", padding: "20px", borderRadius: "var(--radius)", border: "1px solid var(--border)", boxShadow: "var(--shadow-soft)" }}>
                        <h5 style={{ margin: "0 0 16px 0", color: "var(--text-primary)", fontSize: "13px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          3. Quantity Adjustment
                        </h5>

                        {loadingStock ? (
                          <div style={{ fontSize: "13px", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "8px", padding: "16px 0" }}>
                            <span className="page-loading__dot" style={{ width: "8px", height: "8px", margin: 0 }} />
                            Loading current inventory...
                          </div>
                        ) : (
                          <div>
                            {/* Stats */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
                              <div style={{ padding: "14px 18px", background: "var(--surface-input)", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
                                <span style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
                                  Remaining Available Stock
                                </span>
                                <strong style={{ fontSize: "26px", color: (stockInfo?.quantity ?? 0) > 0 ? "var(--text-primary)" : "#dc2626" }}>
                                  {stockInfo?.quantity ?? 0}
                                </strong>
                              </div>
                              <div style={{ padding: "14px 18px", background: "var(--surface-input)", borderRadius: "var(--radius)", border: "1px solid var(--border)", textAlign: "right" }}>
                                <span style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
                                  Last Entered Quantity
                                </span>
                                <strong style={{ fontSize: "26px", color: "var(--text-secondary)" }}>
                                  {stockInfo?.lastEnterdQuantity ?? 0}
                                </strong>
                              </div>
                            </div>

                            {/* Stepper Input */}
                            <div>
                              <label htmlFor="fast-stock-val" style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "8px" }}>
                                New Stock Target Quantity
                              </label>

                              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                <input
                                  id="fast-stock-val"
                                  type="number"
                                  min="0"
                                  value={newStock}
                                  onChange={(e) => setNewStock(e.target.value)}
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: "20px",
                                    fontWeight: 600,
                                    padding: "10px 16px",
                                    width: "140px",
                                    textAlign: "center",
                                    border: "1.5px solid var(--border)",
                                    borderRadius: "var(--radius)",
                                    background: "var(--surface-input)",
                                    color: "var(--text-primary)",
                                  }}
                                  required
                                />

                                <div style={{ display: "flex", gap: "6px" }}>
                                  <button
                                    type="button"
                                    onClick={() => handleQuickAdjust(1)}
                                    className="btn-outline"
                                    style={{ padding: "10px 14px", fontSize: "13px", fontWeight: 500 }}
                                    title="Add 1"
                                  >
                                    +1
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleQuickAdjust(5)}
                                    className="btn-outline"
                                    style={{ padding: "10px 14px", fontSize: "13px", fontWeight: 500 }}
                                    title="Add 5"
                                  >
                                    +5
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleQuickAdjust(10)}
                                    className="btn-outline"
                                    style={{ padding: "10px 14px", fontSize: "13px", fontWeight: 500 }}
                                    title="Add 10"
                                  >
                                    +10
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleQuickAdjust(-1)}
                                    className="btn-outline"
                                    style={{ padding: "10px 14px", fontSize: "13px", fontWeight: 500 }}
                                    title="Subtract 1"
                                  >
                                    -1
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleQuickAdjust(-5)}
                                    className="btn-outline"
                                    style={{ padding: "10px 14px", fontSize: "13px", fontWeight: 500 }}
                                    title="Subtract 5"
                                  >
                                    -5
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Fixed Modal Footer when product is selected */}
              <div className="fast-stock-modal__foot">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={handleClearProduct}
                  disabled={isSaving}
                  style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  <RefreshCw size={14} /> Pick Another Product
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={isSaving || !selectedSizeId || !selectedColorId || loadingStock}
                  onClick={handleSave}
                  style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "0 26px" }}
                >
                  <Save size={16} />
                  {isSaving ? "Saving..." : "Save Stock"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
